/**
 * Payment dispatch providers as data: what each one is called, which queue executes its
 * sends, which rail its settlements are recorded under and which rails it can offer.
 *
 * A provider is registered once, in the provider registry of `@erp/services/payments`, from
 * one of these descriptors. Everything that used to list providers by hand (the Swagger
 * enums, the provider → queue maps, the settlement rail check, the origin-account rails)
 * reads that registry instead, so a new provider is one descriptor, one registration and one
 * queue definition. The id is therefore an open string: a provider registered at runtime is
 * not known to the compiler, and the server refuses an id the registry does not hold.
 */

import type { PaymentInstructionKind } from '../generated/enums.js';

/** Id of a payment dispatch provider, as registered in the provider registry. */
export type PaymentDispatchProviderId = string;

/** ISO 4217 code of a currency a provider sends payments in. */
export type PaymentCurrencyCode = string;

const BRAZILIAN_INSTRUCTION_KINDS = [
  'PIX_KEY',
  'PIX_DYNAMIC_QR',
  'BANK_TRANSFER',
  'BILLET',
  'UTILITY_BILLET',
  'TAX_BARCODE',
  'DARF',
  'GPS',
  'FGTS',
  'GARE',
  'IPVA',
  'DPVAT',
  'PAYCHECK',
] as const satisfies readonly PaymentInstructionKind[];

/** Every instruction kind the capability rows know, in the order the provisioners open them. */
export const ALL_PAYMENT_INSTRUCTION_KINDS = [
  ...BRAZILIAN_INSTRUCTION_KINDS,
  'ACH',
] as const satisfies readonly PaymentInstructionKind[];

/**
 * The instruction kinds of the Brazilian rails (PIX, transfers, boletos, tax guides, payroll).
 * Catalogs that mean "everything Brazilian" (Finnet) use this list, so a kind added for another
 * country (ACH) is never claimed by them.
 */
export const BRAZIL_PAYMENT_INSTRUCTION_KINDS = BRAZILIAN_INSTRUCTION_KINDS;

// A kind added to the enum must be added to the list above, or the catalogs that declare
// "everything" would silently stop opening a row for it.
type MissingInstructionKind = Exclude<
  PaymentInstructionKind,
  (typeof ALL_PAYMENT_INSTRUCTION_KINDS)[number]
>;
const _allInstructionKindsListed: MissingInstructionKind extends never ? true : never = true;
void _allInstructionKindsListed;

// A kind that is not Brazilian must be named here, so adding one to the enum forces a decision.
type NonBrazilianInstructionKind = Exclude<
  PaymentInstructionKind,
  (typeof BRAZIL_PAYMENT_INSTRUCTION_KINDS)[number]
>;
const _nonBrazilianKindsKnown: NonBrazilianInstructionKind extends 'ACH' ? true : never = true;
void _nonBrazilianKindsKnown;

/**
 * The rail a provider's settlement evidence is recorded under on the payment history, and
 * that a duplicate settlement is compared against. `CNAB` is the bank-file return rail,
 * which has no dispatch provider; every other rail belongs to a registered provider.
 */
export type PaymentSettlementRail = string;

/** Rail of the settlements read from bank-file (CNAB) returns. Not a dispatch provider. */
export const BANK_FILE_SETTLEMENT_RAIL = 'CNAB' satisfies PaymentSettlementRail;

/** Transfer rails a provider can send from an origin account. */
export interface PaymentProviderRails {
  pixKey: boolean;
  pixDynamicQr: boolean;
  pixBankData: boolean;
  ted: boolean;
}

/**
 * How a provider reaches the bank, which decides what its capability channel is and how an
 * account is activated (ADR 0033). A `VAN` (bank-file hub) is one channel per origin bank
 * and every account holds its own agreement with the bank; an `API` is one channel for the
 * whole provider and an account inherits it through an active connection.
 */
export type PaymentProviderChannelType = 'VAN' | 'API';

/** The `bankCode` of the channel row of an `API` provider: one channel, whatever the bank. */
export const API_CHANNEL_BANK_CODE = '*';

export interface PaymentDispatchProviderDescriptor {
  /**
   * The id stored on the origin account (`paymentSetupProvider`), on provider attempts, on
   * dispatch manifests and on integration mappings. Never renamed once used.
   */
  readonly id: PaymentDispatchProviderId;
  /**
   * i18n key of the label an operator sees for this provider. Operator copy never names a
   * vendor, so the built-in providers all point at the generic "gateway de pagamento".
   */
  readonly labelKey: string;
  /** The BullMQ queue whose workers execute this provider's sends. */
  readonly queueName: string;
  /**
   * The rail this provider's settlements are recorded under, or null when it never settles
   * a payment itself (Kamino hands the payment to an external ERP that settles it there).
   */
  readonly settlementRail: PaymentSettlementRail | null;
  /** `VAN` for a bank-file hub (channel per origin bank), `API` for a provider's own API. */
  readonly channelType: PaymentProviderChannelType;
  /**
   * The catalog: every instruction kind this provider's builders can put on the wire, read
   * from the vendor's documentation and kept in step with the code. A kind outside it is
   * never provisioned on an account and never released, whatever a capability row says.
   * The release of a kind on one account is the capability row's business, not the catalog's.
   */
  readonly instructions: readonly PaymentInstructionKind[];
  /** Currencies it pays in. The Brazilian providers pay BRL only; Brex pays USD only. */
  readonly currencies: readonly PaymentCurrencyCode[];
  /**
   * The transfer rails it can offer an origin account set up with it, as the destination
   * picker understands them. Always within `instructions` (`railsWithinInstructions`): PIX by
   * bank data and TED are two faces of `BANK_TRANSFER`, which is why the rails are declared
   * rather than derived. A provider whose answer depends on the account (TecnoSpeed probes
   * its dispatch rules and capability routes) registers a resolver that answers within these.
   */
  readonly rails: PaymentProviderRails;
}

/** The label every built-in provider shows an operator: the generic payment gateway. */
const GATEWAY_LABEL_KEY = 'listPage.bulkActions.gatewayLabel';

const BRL = ['BRL'] as const satisfies readonly PaymentCurrencyCode[];
const USD = ['USD'] as const satisfies readonly PaymentCurrencyCode[];

/** Whether a descriptor declares an instruction kind in its catalog. */
export function providerDeclaresInstruction(
  descriptor: Pick<PaymentDispatchProviderDescriptor, 'instructions'>,
  instruction: PaymentInstructionKind,
): boolean {
  return descriptor.instructions.includes(instruction);
}

/**
 * The rails a catalog can stand behind: a PIX rail needs its PIX kind, both transfer rails
 * need `BANK_TRANSFER`. A descriptor whose `rails` claim more than this is inconsistent.
 */
export function railsWithinInstructions(
  descriptor: Pick<PaymentDispatchProviderDescriptor, 'instructions' | 'rails'>,
): boolean {
  const declares = (kind: PaymentInstructionKind) => providerDeclaresInstruction(descriptor, kind);
  const { rails } = descriptor;
  return (
    (!rails.pixKey || declares('PIX_KEY')) &&
    (!rails.pixDynamicQr || declares('PIX_DYNAMIC_QR')) &&
    (!rails.pixBankData || declares('BANK_TRANSFER')) &&
    (!rails.ted || declares('BANK_TRANSFER'))
  );
}

/** Kamino: PIX (key or Copia e Cola) and TED (FormaPagamento 3/2); no PIX by bank data. */
export const KAMINO_PAYMENT_DISPATCH_PROVIDER = {
  id: 'kamino',
  labelKey: GATEWAY_LABEL_KEY,
  queueName: 'kamino-payment-dispatch',
  settlementRail: null,
  channelType: 'API',
  instructions: ['PIX_KEY', 'PIX_DYNAMIC_QR', 'BANK_TRANSFER'],
  currencies: BRL,
  rails: { pixKey: true, pixDynamicQr: true, pixBankData: false, ted: true },
} as const satisfies PaymentDispatchProviderDescriptor;

/** Itaú Pagamentos PIX API: PIX only, by key or BR Code. */
export const ITAU_PAYMENT_DISPATCH_PROVIDER = {
  id: 'itau',
  labelKey: GATEWAY_LABEL_KEY,
  queueName: 'itau-pix-payments',
  settlementRail: 'ITAU_PIX',
  channelType: 'API',
  instructions: ['PIX_KEY', 'PIX_DYNAMIC_QR'],
  currencies: BRL,
  rails: { pixKey: true, pixDynamicQr: true, pixBankData: false, ted: false },
} as const satisfies PaymentDispatchProviderDescriptor;

/**
 * TecnoSpeed (bank-file hub): every rail its dispatch rules and capability routes allow. Its
 * classifier (`resolveTecnospeedPaymentInstructionKind`) names ten kinds plus the dynamic
 * BR Code (form 47); GARE, IPVA and DPVAT travel as `TAX_BARCODE` on its billet endpoint.
 */
export const TECNOSPEED_PAYMENT_DISPATCH_PROVIDER = {
  id: 'tecnospeed',
  labelKey: GATEWAY_LABEL_KEY,
  queueName: 'tecnospeed-payments',
  settlementRail: 'TECNOSPEED',
  channelType: 'VAN',
  instructions: [
    'PIX_KEY',
    'PIX_DYNAMIC_QR',
    'BANK_TRANSFER',
    'BILLET',
    'UTILITY_BILLET',
    'TAX_BARCODE',
    'DARF',
    'GPS',
    'FGTS',
    'PAYCHECK',
  ],
  currencies: BRL,
  rails: { pixKey: true, pixDynamicQr: true, pixBankData: true, ted: true },
} as const satisfies PaymentDispatchProviderDescriptor;

/**
 * Finnet (bank-file hub, ADR 0027): one `POST /payment` per payment per attempt. Its payload
 * builders cover every kind (PIX, transfers and SALARIO, bar codes, the tax family), so the
 * catalog is the full list; its rails grow one payment family at a time, each behind
 * capability rows that start blocked: PIX by key and PIX copia e cola first.
 */
export const FINNET_PAYMENT_DISPATCH_PROVIDER = {
  id: 'finnet',
  labelKey: GATEWAY_LABEL_KEY,
  queueName: 'finnet-payments',
  settlementRail: 'FINNET',
  channelType: 'VAN',
  instructions: BRAZIL_PAYMENT_INSTRUCTION_KINDS,
  currencies: BRL,
  rails: { pixKey: true, pixDynamicQr: true, pixBankData: false, ted: false },
} as const satisfies PaymentDispatchProviderDescriptor;

/**
 * The ids of the built-in providers, for the few places that need a typed fallback when a
 * response names no provider. Never a list to validate against: that is the registry's job
 * (`paymentProviderRegistry` in `@erp/services/payments`), which is where providers are listed.
 */
export type BuiltInPaymentDispatchProviderId =
  | typeof KAMINO_PAYMENT_DISPATCH_PROVIDER.id
  | typeof ITAU_PAYMENT_DISPATCH_PROVIDER.id
  | typeof TECNOSPEED_PAYMENT_DISPATCH_PROVIDER.id
  | typeof FINNET_PAYMENT_DISPATCH_PROVIDER.id;

/**
 * BTG Pactual Empresas (ADR 0032): the bank's own API, one batch per send, every payment then
 * awaiting approval inside the bank's app. The id is the connection's provider id
 * (`BTG_PROVIDER_ID`). The catalog is what the bank's payments API documents and the client
 * builds: PIX by key, PIX copia e cola, transfers (PIX by bank data `PIX_MANUAL` and TED),
 * boletos, utility bills, bar-coded taxes, DARF and FGTS. Each kind is sent only once its
 * capability row on the account lets it through.
 */
export const BTG_PAYMENT_DISPATCH_PROVIDER = {
  id: 'btg',
  labelKey: GATEWAY_LABEL_KEY,
  queueName: 'btg-payments',
  settlementRail: 'BTG',
  channelType: 'API',
  instructions: [
    'PIX_KEY',
    'PIX_DYNAMIC_QR',
    'BANK_TRANSFER',
    'BILLET',
    'UTILITY_BILLET',
    'TAX_BARCODE',
    'DARF',
    'FGTS',
  ],
  currencies: BRL,
  rails: { pixKey: true, pixDynamicQr: true, pixBankData: true, ted: true },
} as const satisfies PaymentDispatchProviderDescriptor;

/**
 * Brex Payments API (USD): one ACH transfer per payment from a Brex Cash account to a vendor's
 * US bank account. The provider has no PIX or TED rail, so every rail is off; ACH is a kind of
 * its own.
 */
export const BREX_PAYMENT_DISPATCH_PROVIDER = {
  id: 'brex',
  labelKey: GATEWAY_LABEL_KEY,
  queueName: 'brex-payments',
  settlementRail: 'BREX',
  channelType: 'API',
  instructions: ['ACH'],
  currencies: USD,
  rails: { pixKey: false, pixDynamicQr: false, pixBankData: false, ted: false },
} as const satisfies PaymentDispatchProviderDescriptor;
